import Ajv from 'ajv';
import { z } from 'zod';
import {
  CandidateOpportunitiesConst,
  PositionsConst,
  TaxonomyConst,
  WeeklyTimeUnits,
} from '@axiom/const';
import { SchemaDate, SchemaTimestamp, ZodArrayToEnum } from '@axiom/types';

import { axiomValidationOptions } from './options';
import { addCustomKeywords } from './keywords';
import { TagSchema } from './tag';

const { BusinessTitlesForLegacyJsApiUseOnly } = TaxonomyConst;
const { RollOffReasonsCodes } = CandidateOpportunitiesConst;

export const PositionReservedTypes = Object.values(PositionsConst.ReservedType);
export const PositionEndDateStatuses = ['Confirmed', 'Not Confirmed'] as const;

export const PositionOtherCandidatesStatusOnPositionStatuses = Object.values(
  CandidateOpportunitiesConst.CandidateStatuses
);

export const PositionRoles = Object.values(PositionsConst.Roles);

export const PositionWeeklyTimeUnits = Object.values(WeeklyTimeUnits);
export const PositionWorksites = Object.values(PositionsConst.Worksites);
export type PositionWorksitesType =
  | (typeof PositionsConst.Worksites)[keyof typeof PositionsConst.Worksites]
  | null
  | undefined;

export const PositionOtherCandidatesStatusesSchema = z.object({
  status: z.enum(
    ZodArrayToEnum(PositionOtherCandidatesStatusOnPositionStatuses)
  ),
  candidateName: z.string().nullable(),
});

export const PositionSchema = z.object({
  aiDescription: z.string().nullish(),
  baselineBillRate: z.number().nullable(),
  baselineCost: z.number().nullable(),
  baselineMargin: z.number().nullable(),
  billingHoursPerDay: z.number().nullable(),
  billingHoursPerWeek: z.number().nullable(),
  billingUnitsPerWeek: z.number().nullable(),
  businessTitle: z.string().nullable(),
  createdAt: SchemaTimestamp.nullable(),
  demandDriver: z.string().max(255).nullable(),
  description: z.string().nullable(),
  endDate: SchemaDate.nullable(),
  endDateStatus: z.enum(PositionEndDateStatuses).nullable(),
  estimatedEngagementTotalMonths: z.number().nullable(),
  id: z.string().uuid(),
  integrationId: z.string().max(255).nullable(),
  isPremium: z.boolean().nullish(),
  name: z.string().max(255).nullable(),
  opportunityId: z.string().uuid().nullable(),
  otherCandidatesStatusOnPosition: z
    .array(PositionOtherCandidatesStatusesSchema)
    .nullable(),
  product: z.string().max(255).nullable(),
  reservedType: z.enum(ZodArrayToEnum(PositionReservedTypes)).nullable(),
  role: z.enum(ZodArrayToEnum(PositionRoles)).nullable(),
  salesforceId: z.string().max(255).nullable(),
  startDate: SchemaDate.nullable(),
  tags: z.array(TagSchema).nullish(),
  talentExperienceYears: z.string().max(255).nullable(),
  updatedAt: SchemaTimestamp.nullable(),
  weeklyTimeUnit: z.enum(ZodArrayToEnum(PositionWeeklyTimeUnits)).nullable(),
  worksite: z.enum(ZodArrayToEnum(PositionWorksites)).nullable(),
});

export type Position = z.infer<typeof PositionSchema>;

const ajv = new Ajv({
  ...axiomValidationOptions(),
  coerceTypes: true,
});

// add parseFloat support
addCustomKeywords(ajv);

const positionCommonValidation = {
  type: 'object',
  additionalProperties: false,
  properties: {
    id: {
      type: 'string',
      format: 'uuid',
    },
    businessTitle: {
      type: ['string', 'null'],
      enum: [...Object.keys(BusinessTitlesForLegacyJsApiUseOnly), null],
    },
    opportunityId: {
      type: 'string',
      format: 'uuid',
    },
    name: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    product: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    role: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    startDate: {
      type: ['string', 'null'],
      format: 'date',
    },
    endDate: {
      type: ['string', 'null'],
      format: 'date',
    },
    baselineMargin: {
      type: ['number', 'null'],
      maximum: Number.MAX_SAFE_INTEGER,
    },
    baselineBillRate: {
      type: ['number', 'null'],
      maximum: Number.MAX_SAFE_INTEGER,
    },
    baselineCost: {
      type: ['number', 'null'],
      maximum: Number.MAX_SAFE_INTEGER,
    },
    demandDriver: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    billingUnitsPerWeek: {
      // TODO: remove string as a type once Boomi is sending as numeric
      type: ['number', 'string', 'null'],
      parseFloat: {
        type: 'number',
      },
    },
    aiDescription: {
      type: ['string', 'null'],
    },
    description: {
      type: ['string', 'null'],
    },
    talentExperienceYears: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    worksite: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    salesforceId: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    weeklyTimeUnit: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    billingHoursPerDay: {
      type: ['number', 'null'],
      maximum: 24,
    },
    reservedType: {
      type: ['string', 'null'],
      enum: [...Object.values(PositionsConst.ReservedType), null],
    },
    integrationId: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    isPremium: {
      type: ['boolean'],
    },
    endDateStatus: {
      type: ['string', 'null'],
      enum: [...PositionEndDateStatuses, null],
    },
  },
};

export const upsertPositionValidatorCreator = ({
  ajvInstance = ajv,
  additionalOpts = {},
} = {}) =>
  ajvInstance.compile({
    ...positionCommonValidation,
    ...additionalOpts,
    anyOf: Object.keys(positionCommonValidation.properties).map(key => ({
      required: [key],
    })),
  });

const positionBoomiValidation = {
  type: 'object',
  additionalProperties: false,
  properties: {
    ...positionCommonValidation.properties,
    rollOffReason: {
      type: ['string', 'null'],
      enum: [...Object.values(RollOffReasonsCodes), null],
    },
    proposedMarginTarget: {
      type: ['number', 'null'],
      maximum: Number.MAX_SAFE_INTEGER,
    },
    proposedHourlyCompensation: {
      type: ['number', 'null'],
      maximum: Number.MAX_SAFE_INTEGER,
    },
    proposedHourlyCompCandidateCurrency: {
      type: ['number', 'null'],
      maximum: Number.MAX_SAFE_INTEGER,
    },
    proposedHourlyRate: {
      type: ['number', 'null'],
      maximum: Number.MAX_SAFE_INTEGER,
    },
  },
};

export const upsertPositionBoomiValidatorCreator = () => {
  return ajv.compile({
    ...positionBoomiValidation,
    additionalProperties: false,
    anyOf: Object.keys(positionBoomiValidation.properties).map(key => ({
      required: [key],
    })),
  });
};

// ONLY CONDITIONALLY USE THIS - for boomi endpoints only
export const upsertPositionAdditionalPropertiesValidator =
  upsertPositionValidatorCreator({
    ajvInstance: new Ajv({
      ...axiomValidationOptions(),
      removeAdditional: true,
    }),
    additionalOpts: {
      additionalProperties: false,
    },
  });

export const upsertPositionBoomiValidator =
  upsertPositionBoomiValidatorCreator();
export const upsertPositionValidator = upsertPositionValidatorCreator();

export const filterPositionValidator = ajv.compile({
  ...positionCommonValidation,
  anyOf: Object.keys(positionCommonValidation.properties).map(key => ({
    required: [key],
  })),
});

export const positionsStatsFilterValidator = ajv.compile({
  type: 'object',
  additionalProperties: false,
  properties: {
    ...positionCommonValidation.properties,
    startDate: {
      type: 'object',
      additionalProperties: false,
      properties: {
        start: {
          type: 'string',
          format: 'date',
        },
        end: {
          type: 'string',
          format: 'date',
        },
      },
    },
    'opportunity.stageCode': {
      type: 'array',
      items: {
        type: ['string', 'null'],
        maxLength: 255,
      },
    },
  },
});
